import React, { useCallback, useEffect, useMemo, useState } from "react"

import { Divider, Flex, Space, Typography } from "antd"
import dayjs from "dayjs"
import FileDownload from "js-file-download"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import ExportDropdown from "../../../Components/Export"
import Filters from "../../../Components/Filters"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import Icon from "../../../Icon"
import { alertsColumnIconMap, dateFormat, serializeData } from "../../../Utils/utils"
import RequestDetailsDrawer from "./components/RequestDetailsDrawer/RequestDetailsDrawer"
import { RECEIPT_STATUS } from "./components/StatusTag/StatusTag"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import { PROGRAM_META } from "./constants"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const pageSize = 50
const { Title } = Typography
const pinnedColumns = ["#"]

const RequestsDigitalReceiptPage = ({
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  isFiltersDefault,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const folder = new URLSearchParams(search).get("folder")

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [openedRecord, setOpenedRecord] = useState(null)

  const { count, results } = result || {}
  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  // In the "Update info" folder every row is needs_update, so the Status filter would
  // only ever produce the empty state; hide it there.
  const visibleFilters =
    folder === "update_info"
      ? { ...visibleFiltersAdmin, request_status: false }
      : visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const updateData = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    props.getInfo({
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      folder,
      ...(columns.length && { columns: ["sequence_number", "id", ...columns].join(",") }),
      // Raw filter values so the dummy layer can filter client-side (no backend yet).
      _filters: filters?.asMutable ? filters.asMutable({ deep: true }) : { ...filters }
    })
  }

  useEffect(() => {
    document.title = "Requests Digital Receipt - Chargeback"
  }, [])

  // Switching the "Update info" sub-folder resets to the first page (guarded so it does
  // not double-dispatch the fetch when already on page 1).
  useEffect(() => {
    if (pageNumber !== 1) setPageNumber(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder])

  useEffect(() => {
    updateData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, ordering, pageNumber, filters, myColumnsHash])

  const handleStatusChange = useCallback(({ id, newStatus }) => {
    props.updateStatus({ id, status: newStatus })
    setOpenedRecord((prev) =>
      prev && prev.id === id ? { ...prev, status: newStatus, request_status: newStatus } : prev
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // In admin, the business account name links out to that account's admin view.
  const handleBusinessAccount = useCallback((record) => {
    if (record?.business_account_id)
      navigate(`/admin/dashboard?business_account_id=${record.business_account_id}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleReview = useCallback((id, action) => {
    props.reviewUpdate({ id, action })
    setOpenedRecord((prev) => {
      if (!prev || prev.id !== id) return prev
      const next = action === "approve" ? "enrolled" : prev.status
      return {
        ...prev,
        has_pending_update: false,
        profile_update: null,
        status: next,
        request_status: next
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getTableColumns = useMemo(() => {
    const allColumns = getColumns({
      onStatusChange: handleStatusChange,
      onBusinessAccount: handleBusinessAccount
    })
    return [
      allColumns["id"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onView: setOpenedRecord })
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myColumnsHash])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: count ? "max-content" : 230 * myColumnsLength,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)",
        scrollToFirstRowOnChange: true
      },
      pagination: { total: count, current: pageNumber, pageSize },
      locale: {
        emptyText: isFiltersDefault ? (
          <EmptyComponent type="no_data_available" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type="menu_search" />}
              onClick={props.clearFilters}
            >
              {t("Clear search")}
            </Button>
          </EmptyComponent>
        )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myColumnsLength, count, pageNumber, isFiltersDefault]
  )

  const handleChangeTable = useCallback((pagination, tableFilters, sorter) => {
    let newOrdering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        newOrdering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else if (sorter.order) {
        newOrdering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
      }
    }
    setPageNumber(pagination.current)
    setOrdering(newOrdering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleExport = useCallback(() => {
    const headers = [
      "#",
      "Status",
      "Programs",
      "Request date",
      "Update status date",
      "Business account",
      "User full name",
      "User email"
    ]
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`
    const rows = (results || []).map((r) =>
      [
        r.sequence_number,
        RECEIPT_STATUS[r.status]?.label || r.status,
        (r.programs || []).map((p) => PROGRAM_META[p]?.label || p).join(" / "),
        r.created_timestamp ? dayjs.utc(r.created_timestamp).format(dateFormat) : "",
        r.updated_timestamp ? dayjs.utc(r.updated_timestamp).format(dateFormat) : "",
        r.business_account,
        r.user_full_name,
        r.user_email
      ]
        .map(escape)
        .join(",")
    )
    const csv = [headers.map(escape).join(","), ...rows].join("\n")
    FileDownload(csv, `digital-receipt-requests${folder ? `-${folder}` : ""}.csv`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, folder])

  const onRow = useCallback((record) => ({ onClick: () => setOpenedRecord(record) }), [])

  const headline = folder === "update_info" ? t("Update info") : t("Digital receipt")

  return (
    <div className="requests-page digital-receipt-requests">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`${headline}: ${count || 0}`}
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"digital_receipt"}
          columnIconMap={{ ...alertsColumnIconMap, programs: "card" }}
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          {/* Single Export button (exports the current view). The two-option admin
              dropdown ("all" vs "as filtered") needs the backend export endpoint to
              distinguish them; until then a single honest button avoids mislabeling. */}
          <ExportDropdown loading={false} onExport={handleExport} isAdmin={false} />
          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        locale={tableSettings.locale}
        onRow={onRow}
        rowClassName={() => "clickable-row"}
      />

      <RequestDetailsDrawer
        record={openedRecord}
        open={!!openedRecord}
        onClose={() => setOpenedRecord(null)}
        onReview={handleReview}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  visibleFiltersAdmin: state.requestsDigitalReceipt.visibleFiltersAdmin,
  defaultColumnsAdmin: state.requestsDigitalReceipt.defaultColumnsAdmin,
  columnsAdmin: state.requestsDigitalReceipt.columnsAdmin,
  fetching: state.requestsDigitalReceipt.fetching,
  result: state.requestsDigitalReceipt.result,
  filters: state.requestsDigitalReceipt.filters,
  filterOptions: state.requestsDigitalReceipt.filterOptions,
  isFiltersDefault: selectAreFiltersDefault(state)
})

const mapDispatchToProps = (dispatch) => ({
  getInfo: (params) => dispatch(Creators.getRequestsDigitalReceiptRequest(params)),
  updateStatus: (params) => dispatch(Creators.updateRequestsDigitalReceiptStatusRequest(params)),
  reviewUpdate: (params) => dispatch(Creators.reviewDigitalReceiptUpdateRequest(params)),
  changeColumns: (key, columns) => dispatch(Creators.requestsDigitalReceiptSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.requestsDigitalReceiptSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.requestsDigitalReceiptSetProp(visibleFiltersType, filters)),
  clearFilters: () => dispatch(Creators.clearRequestsDigitalReceiptFilters())
})

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(RequestsDigitalReceiptPage))
